import { createContext, FC, PropsWithChildren } from 'react';

import { useFmsTextsQuery } from '@/api/fms/fmsApi';
import { useUserSettingsQuery } from '@/api/userSettings/userSettingsApi';
import { getAuth } from '@/auth/Auth';
import { findLanguageFromAmr } from '@/auth/utils/findLanguageFromAmr';
import { Language } from '@/i18n/Language';
import { FMSTexts } from '@/types/fms';

export const TextsContext = createContext<FMSTexts | undefined>(undefined);

/**
 * Make the texts available to the application.
 */
export const TextsProvider: FC<PropsWithChildren> = ({ children }) => {
  // This will return HTTP 403 Forbidden if the user is not a Connect user.
  const userSettings = useUserSettingsQuery();

  // In case the we are not able to get the user settings,
  // we will use the language from the AMR (Authentication Method Reference)
  // to get an approximate correct language for the user.
  const amr = getAuth().getTokenParsed().amr;
  const languageFromAmr = findLanguageFromAmr(amr);

  const userLanguage = userSettings.data?.language;

  const fmsTextsQuery = useFmsTextsQuery(
    userLanguage || languageFromAmr || Language.English,
    {
      // We want to primarily get the language from the user settings.
      // Thus, we skip the query if the user settings are still loading.
      skip: userSettings.isLoading,
    },
  );

  if (userSettings.isLoading || fmsTextsQuery.isLoading) {
    // No need to display a loading state.
    // The calls should be fast enough that the user
    // does not notice the loading.
    return null;
  }

  return (
    <TextsContext.Provider value={fmsTextsQuery.data}>
      {children}
    </TextsContext.Provider>
  );
};
